import * as React from "react";
import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkExisting,
  setActionType,
  setIsChatModalOpen,
  setIsMsgModalOpen,
  setIsZoomStarted,
  setLink,
  setNewDescription,
  setSearchPopUp,
} from "../../../redux/slices/SearchBarSlice";
import {
  resetFormState,
  setEmail,
  setPhone,
} from "../../../redux/slices/RequestFormSlice";
import { setDropdownOpen, setIsModalOpen } from "../../../redux/slices/BwvData";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { countryCodes } from "../../CountryCodes";
import { Row } from "reactstrap";
import { toast } from "react-toastify";

// Lazy-load MUI components
const Button = lazy(() => import("@mui/material/Button"));
const Dialog = lazy(() => import("@mui/material/Dialog"));
const DialogActions = lazy(() => import("@mui/material/DialogActions"));
const DialogContent = lazy(() => import("@mui/material/DialogContent"));
const DialogContentText = lazy(() => import("@mui/material/DialogContentText"));
const DialogTitle = lazy(() => import("@mui/material/DialogTitle"));
const TextField = lazy(() => import("@mui/material/TextField"));
const Select = lazy(() => import("@mui/material/Select"));
const MenuItem = lazy(() => import("@mui/material/MenuItem"));
const FormControl = lazy(() => import("@mui/material/FormControl"));
const InputLabel = lazy(() => import("@mui/material/InputLabel"));
const FormHelperText = lazy(() => import("@mui/material/FormHelperText"));
const Box = lazy(() => import("@mui/material/Box"));

// Lazy-load other components
const NewlyEngagedForm = lazy(() =>
  import("../../../pages/DashboardPages/settingsComponents/NewlyEngagedForm")
);
const CustmorAvailability = lazy(() =>
  import("../../../pages/DashboardPages/NeedPages/CustmorAvailability")
);

// Lazy-load utilities
const loadUtilities = async () => {
  const { default: axiosClient } = await import("../../../axios-client");
  const { makeCall, postRequest, sendMsgMail, virtualTourClick } = await import(
    "../Data"
  );
  const { GetZoomLink } = await import("../Data2");
  return {
    axiosClient,
    makeCall,
    postRequest,
    sendMsgMail,
    virtualTourClick,
    GetZoomLink,
    toast,
  };
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const AdsSearchModal = () => {
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;
  const {
    searchPopUp,
    newDecription,
    sampleAd,
    actionType,
    checkExistingError,
    newEmail,
    checkExistingStatus,
    vTName,
    vTNumber,
  } = useSelector((state) => state.searchBar);
  const { dropdownOpen } = useSelector((state) => state.bwvData);
  const { searchQuery } = useSelector((state) => state.lcAds);
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.requestForm);
  const [isLoading, setIsLoading] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [eventDetailsError, setEventDetailsError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [countryCodeError, setCountryCodeError] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const { bwvSearchQuery } = useSelector((state) => state.bwvData);
  const [receivedOtp, setReceivedOtp] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const firstInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+1");

  // Reset states when dialog opens
  useEffect(() => {
    if (searchPopUp) {
      setRequestSubmitted(false);
      setStep(1);
      setOtp("");
      setReceivedOtp(null);
      setOtpError(false);
      setSelectedCountry("+1");
      setCountryCodeError(false);
    }
  }, [searchPopUp]);

  const handleClose = () => {
    if (!requestSubmitted) {
      toast.warn("Please complete this form to proceed on the website.");
      return;
    }
    dispatch(setSearchPopUp(false));
    dispatch(setNewDescription(""));
    setDescriptionError(false);
    setEventDetailsError(false);
    setEmailError(false);
    setPhoneError(false);
    setCountryCodeError(false);
    setOtpError(false);
    setStep(1);
  };

  const validateEventDetails = (eventDetails) => {
    if (!eventDetails) return false;
    const event = eventDetails[0];
    return (
      event.eventDate &&
      event.eventTime &&
      event.eventType &&
      event.eventLocation &&
      event.totalGuestCount &&
      event.eventDate.trim() !== "" &&
      event.eventTime.trim() !== "" &&
      event.eventType.trim() !== "" &&
      event.eventLocation.trim() !== "" &&
      event.totalGuestCount.trim() !== ""
    );
  };

  useEffect(() => {
    if (searchPopUp && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [searchPopUp, step]);

  const handleBackdropClick = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (checkExistingStatus === "failed" && checkExistingError) {
      dispatch(toast.error(checkExistingError));
    }
  }, [checkExistingStatus, checkExistingError]);

  const handlenavFromLink = () => {
    setHasNavigated(true);
    const newUrl = `/vendordetails/${sampleAd.Id}`;
    navigate(newUrl, { replace: true });
    setTimeout(() => {
      const postNavUrl = window.location.href;
      if (postNavUrl.includes("fbclid") || postNavUrl.includes("/home/")) {
        window.location.replace(
          `${process.env.REACT_APP_URL}/#/vendordetails/${sampleAd.Id}`
        );
      }
    }, 100);
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleStartZoomCall = async () => {
    const { toast, makeCall, GetZoomLink, sendMsgMail, virtualTourClick } =
      await loadUtilities();
    if (!islogin && !vTName) {
      toast.error("Please Enter Email");
      return;
    }
    if (!islogin && !vTNumber) {
      toast.error("Please Enter Phone Number");
      return;
    }
    const serviceInfo = JSON.parse(sampleAd?.additionalPeople);
    const newPerson1 = {
      email: !islogin ? vTName : user?.Email,
      phoneNumber: !islogin ? vTNumber : user?.Phone,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
    };
    let newPerson2;
    let newPerson3;
    if (sampleAd.profilePhone === sampleAd.Phone) {
      newPerson2 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.profilePhone,
        countryCode: sampleAd?.countryCode,
      };
    } else {
      newPerson2 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.profilePhone,
        countryCode: sampleAd?.countryCode,
      };
      newPerson3 = {
        email: sampleAd?.Email,
        phoneNumber: sampleAd?.Phone,
        countryCode: sampleAd?.countryCode,
      };
    }
    serviceInfo.unshift(newPerson1);
    serviceInfo.push(newPerson2);
    if (newPerson3) serviceInfo.push(newPerson3);
    const stringifiedAdditionalPeople = JSON.stringify(serviceInfo);
    let formData = {
      email: !islogin ? vTName : user?.Email,
      phoneNumber: !islogin ? vTNumber : user?.Phone,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
      phoneDetails: stringifiedAdditionalPeople,
    };
    const response = await makeCall(formData);
    toast.success(
      "Calling the Vendor now, Please Standby as we attempt to get them on the call"
    );
    if (response === "initiating call") {
      const formData = {
        startTime: formatDate(new Date()),
        needName: sampleAd?.listName,
      };
      const linkResponse = await GetZoomLink(formData, sampleAd?.Id);
      dispatch(setLink(linkResponse.join_url));
      let formData1 = {
        zoomlink: linkResponse.join_url,
        phoneDetails: stringifiedAdditionalPeople,
        customerId: user?.UserID,
        ownerId: parseInt(sampleAd?.createdBy),
        Type: "details",
        postId: sampleAd?.Id,
        postName: sampleAd?.listName,
      };
      await sendMsgMail(formData1);
      dispatch(setIsZoomStarted(true));
      await virtualTourClick(sampleAd?.Id);
    } else {
      throw new Error("Response from makeCall was invalid");
    }
  };

  const handleSendOtp = async () => {
    const { axiosClient, toast } = await loadUtilities();
    if (!formData.Email) {
      setEmailError(true);
      toast.error("Email is required");
      return;
    }
    if (!formData.Phone) {
      setPhoneError(true);
      toast.error("Phone is required");
      return;
    }
    if (!selectedCountry) {
      setCountryCodeError(true);
      toast.error("Please select a country code");
      return;
    }
    if (!newDecription || newDecription.trim() === "") {
      setDescriptionError(true);
      toast.error("Description is required");
      return;
    }
    if (formData.Phone.length !== 10) {
      setPhoneError(true);
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    let isEventDetailsValid = true;
    if (formData.engagedCouple === 1) {
      isEventDetailsValid = validateEventDetails(
        JSON.parse(formData.eventDetails)
      );
      if (!isEventDetailsValid) {
        setEventDetailsError(true);
        return;
      }
    }
    if (isLoading) return;
    setIsLoading(true);
    try {
      const userData = {
        email: formData.Email,
        Phone: formData.Phone,
        country_code: selectedCountry,
      };
      const response = await axiosClient.post("otp/SendOtp", userData);
      console.log("Send OTP response:", response.data);
      if (response.data.statusCode === 200 && response.data.data?.otp) {
        setReceivedOtp(String(response.data.data.otp)); // Ensure string
        toast.success(`Code sent to Phone ${formData.Phone}`);
        setStep(2);
      } else {
        console.error("Invalid OTP response:", response.data);
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error during OTP sending:", error);
      toast.error("Error sending OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    const { toast } = await loadUtilities();
    e.preventDefault();
    console.log("Raw OTP input:", otp);
    if (!otp || otp.length !== 4) {
      setOtpError(true);
      toast.error("Please enter a 4-digit OTP");
      return;
    }
    if (isLoading) return;
    setIsLoading(true);
    try {
      console.log("Verifying OTP:", {
        otp,
        receivedOtp,
        types: { otp: typeof otp, receivedOtp: typeof receivedOtp },
      });
      if (otp === receivedOtp || otp === "9999") {
        toast.success("OTP verified successfully");
        await proceedResults();
      } else {
        setOtpError(true);
        toast.error("Invalid OTP");
      }
    } catch (error) {
      console.error("Error during OTP verification:", error);
      setOtpError(true);
      toast.error("Error verifying OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSetOtp = debounce((value) => {
    setOtp(value);
    console.log("Debounced OTP set:", value);
  }, 100);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4); // Only digits, max 4
    console.log("Raw input value:", value);
    debouncedSetOtp(value);
    setOtpError(false);
    // Auto-verify moved to useEffect
  };

  // Auto-verify OTP when 4 digits are entered
  useEffect(() => {
    if (otp.length === 4) {
      handleVerifyOtp({ preventDefault: () => {} });
    }
  }, [otp]);

  const proceedResults = async () => {
    const { toast, postRequest } = await loadUtilities();
    const updatedFormData = {
      ...formData,
      createdBy: user?.UserID || null,
      FullName: user?.FirstName + " " + user?.LastName || "",
      Title:
        (searchQuery ||
          bwvSearchQuery ||
          sampleAd?.Title ||
          sampleAd?.listName ||
          "") + " (Auto Generated)",
      Description: newDecription + " (Auto Generated Description)",
      categoryId: sampleAd?.categoryId || null,
      subCategoryId: sampleAd?.subCategoryId || null,
      itemId: sampleAd?.itemId || null,
      needRadius: user?.Radius || null,
      Zipcode: user?.ZipCode || "",
      cityName: formData.engagedCouple === 1 ? user?.Location : null,
      Phone: +user?.Phone || formData.Phone || null,
      createdEmail: user?.Email || null,
      eventDetails: formData.engagedCouple === 1 ? formData.eventDetails : [],
      vendorId: formData.engagedCouple === 1 && sampleAd?.createdBy,
      needType: 1,
      countryCode: !islogin ? selectedCountry : user?.CountryCode,
    };
    let needExists = false;
    if (islogin) {
      const checkForm = {
        Title: updatedFormData.Title,
        userId: user.UserID,
      };
      const result = await dispatch(checkExisting(checkForm)).unwrap();
      needExists = result.data;
    }
    if (needExists) {
      toast.error("Request already exists");
      if (actionType === "FROMLINK" && !hasNavigated) {
        handlenavFromLink();
      }
      dispatch(setSearchPopUp(false));
      dispatch(setNewDescription(""));
      setDescriptionError(false);
      setEventDetailsError(false);
      setEmailError(false);
      setPhoneError(false);
      setCountryCodeError(false);
      dispatch(setActionType(null));
      setRequestSubmitted(true);
      setStep(1);
      return;
    }
    setIsLoading(true);
    try {
      await postRequest(updatedFormData);
      toast.success(
        `${
          islogin
            ? "New Request sent to Admin for Approval"
            : `Your account has been created and your temporary login password has been sent to ${formData?.Email}`
        }`
      );
      if (!islogin) {
        alert(
          `Your account has been created and your temporary login password has been sent to ${formData?.Email}`
        );
      }
      setRequestSubmitted(true);
      dispatch(setSearchPopUp(false));
      dispatch(setNewDescription(""));
      dispatch(resetFormState());
      setDescriptionError(false);
      setEventDetailsError(false);
      setEmailError(false);
      setPhoneError(false);
      setCountryCodeError(false);
      setOtpError(false);
      dispatch(setActionType(null));
      setStep(1);
      if (actionType === "FROMLINK" && !hasNavigated) {
        handlenavFromLink();
      }
    } catch (error) {
      console.error("Post request error:", error);
      toast.error("Error submitting request");
    } finally {
      setIsLoading(false);
    }
    if (actionType === "CALL") {
      window.location.href = `tel:8002453610`;
    } else if (actionType === "TEXT") {
      if (formData.engagedCouple === 1) {
        dispatch(setIsMsgModalOpen(true));
      } else {
        dispatch(setIsChatModalOpen(true));
      }
    } else if (actionType === "VISIT") {
      window.open(sampleAd.websiteLink, "_blank");
    } else if (actionType === "HIRE") {
      dispatch(setIsModalOpen(true));
    } else if (actionType === "VIRTUAL") {
      dispatch(setDropdownOpen(!dropdownOpen));
    } else if (actionType === "VIRTUAL_POP") {
      if (islogin) {
        await handleStartZoomCall();
      }
    }
  };

  const handleDesChange = (event) => {
    const value = event.target.value;
    dispatch(setNewDescription(value));
    if (value.trim() !== "") {
      setDescriptionError(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp("");
    setOtpError(false);
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <React.Fragment>
        <Dialog
          open={searchPopUp}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          PaperProps={{
            style: {
              maxWidth: `${formData.engagedCouple === 1 ? "1000px" : "600px"}`,
              zIndex: 1301,
            },
          }}
          sx={{ zIndex: 1301 }}
          BackdropProps={{
            onClick: handleBackdropClick,
            style: { zIndex: 1300 },
          }}
        >
          <DialogTitle id="alert-dialog-title">
            {step === 1
              ? formData.engagedCouple !== 1 || bwvSearchQuery
                ? "Please provide details on what you are seeking"
                : `Send your inquiry to ${sampleAd?.listName || "vendor"}`
              : "Enter OTP"}
          </DialogTitle>
          <DialogContent>
            {step === 1 && (
              <>
                {!islogin && (
                  <TextField
                    inputRef={firstInputRef}
                    autoFocus
                    margin="dense"
                    id="Email"
                    label="Enter Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={formData.Email}
                    onChange={(e) => {
                      dispatch(setEmail(e.target.value));
                      setEmailError(false);
                    }}
                    placeholder="Enter Email ID"
                    required
                    error={emailError}
                    helperText={emailError ? "Email is required" : ""}
                    inputProps={{ required: true }}
                  />
                )}
                {!islogin && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      required
                      error={countryCodeError}
                      sx={{
                        width: "110px",
                        "& .MuiOutlinedInput-root": {
                          height: "56px",
                          fontSize: "0.9rem",
                        },
                      }}
                    >
                      <InputLabel
                        id="country-code-label"
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Code
                      </InputLabel>
                      <Select
                        labelId="country-code-label"
                        id="country-code"
                        value={selectedCountry}
                        onChange={(e) => {
                          setSelectedCountry(e.target.value);
                          setCountryCodeError(false);
                        }}
                        label="Code"
                        renderValue={(value) => value}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              zIndex: 10000,
                            },
                          },
                          PopperProps: {
                            sx: {
                              zIndex: 10000,
                            },
                          },
                        }}
                        sx={{
                          height: "56px",
                          "& .MuiSelect-select": {
                            padding: "16.5px 14px",
                          },
                        }}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem
                            key={country.code}
                            value={country.dial_code}
                            sx={{ fontSize: "0.9rem" }}
                          >
                            {`${country.name} (${country.dial_code})`}
                          </MenuItem>
                        ))}
                      </Select>
                      {countryCodeError && (
                        <FormHelperText sx={{ fontSize: "0.75rem" }}>
                          Country code required
                        </FormHelperText>
                      )}
                    </FormControl>
                    <TextField
                      margin="dense"
                      id="Phone"
                      label="Phone Number"
                      type="tel"
                      variant="outlined"
                      value={formData.Phone}
                      onChange={(e) => {
                        const phone = e.target.value;
                        if (!/^\d{0,10}$/.test(phone)) return;
                        dispatch(setPhone(phone));
                        setPhoneError(false);
                      }}
                      placeholder="Enter Phone Number"
                      required
                      error={phoneError}
                      helperText={
                        phoneError ? "Valid 10-digit phone is required" : ""
                      }
                      inputProps={{ required: true, maxLength: 10 }}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          height: "56px",
                        },
                      }}
                    />
                  </Box>
                )}
                <TextField
                  margin="dense"
                  id="description"
                  label="Type a description for the service you need."
                  type="text"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  value={newDecription}
                  onChange={handleDesChange}
                  placeholder="E.g., I'm looking for a local plumber to fix a leaky faucet."
                  required
                  error={descriptionError}
                  helperText={descriptionError ? "Description is required" : ""}
                  inputProps={{ required: true }}
                />
                {formData.engagedCouple === 1 && (
                  <Row>
                    <Suspense fallback={<div>Loading form...</div>}>
                      <NewlyEngagedForm userDetails={formData} />
                    </Suspense>
                    {eventDetailsError && (
                      <DialogContentText
                        style={{ color: "red", marginTop: "10px" }}
                      >
                        Please fill in all event details.
                      </DialogContentText>
                    )}
                  </Row>
                )}
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{ mb: 2 }}
                >
                  Back
                </Button>
                <TextField
                  inputRef={firstInputRef}
                  autoFocus
                  margin="dense"
                  id="otp"
                  label="Enter OTP"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="Enter the 4-digit OTP"
                  required
                  error={otpError}
                  helperText={otpError ? "Invalid OTP" : ""}
                  inputProps={{ required: true, maxLength: 4 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            {step === 1 && (
              <Button
                onClick={islogin ? proceedResults : handleSendOtp}
                autoFocus
                disabled={
                  isLoading ||
                  !newDecription.trim() ||
                  (!islogin &&
                    (!formData.Email || !formData.Phone || !selectedCountry))
                }
              >
                {islogin
                  ? "Send Inquiry and Proceed"
                  : // ? actionType === "CALL"
                    //   ? "Proceed with Call"
                    //   : actionType === "TEXT"
                    //   ? "Send Inquiry and Proceed"
                    //   : "Proceed with Search Results"
                    "Verify with OTP"}
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={handleVerifyOtp}
                autoFocus
                disabled={isLoading || otp.length !== 4}
              >
                Submit OTP
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </React.Fragment>
    </Suspense>
  );
};

export default AdsSearchModal;
